import { useEffect, useRef, useState } from "react";
import { getAboutData } from "@/api/aboutApi";
import { assetUrl } from "@/utils/assetUrl";
import { useSEO, buildBreadcrumbLd, SITE_ORIGIN } from "@/utils/seo";
import { readAboutPreload } from "@/utils/aboutPreload";
import FaqBlock from "@/components/feature/FaqBlock";

interface MarqueeRowProps {
  items: { name: string; logo?: string }[];
  speed: number;
  isPaused: boolean;
  offsetStart?: number;
}

function MarqueeRow({ items, speed, isPaused, offsetStart = 0 }: MarqueeRowProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const posRef = useRef(-offsetStart);
  const animFrameRef = useRef<number>(0);
  const tripled = [...items, ...items, ...items];

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const animate = () => {
      if (!isPaused && track) {
        posRef.current -= speed;
        const oneThird = track.scrollWidth / 3;
        if (posRef.current <= -oneThird) {
          posRef.current += oneThird;
        }
        track.style.transform = `translateX(${posRef.current}px)`;
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isPaused, speed]);

  return (
    <div className="overflow-hidden w-full">
      <div ref={trackRef} className="flex w-max">
        {tripled.map((p, i) => (
          <div
            key={`${p.name}-${i}`}
            className="flex-shrink-0 h-20 border-r border-b border-gray-100 flex flex-col items-center justify-center gap-1.5 bg-white hover:bg-[#f8fafc] transition-colors duration-200 cursor-default"
            style={{ width: "160px" }}
          >
            <div className="w-16 h-8 flex items-center justify-center overflow-hidden">
              {p.logo ? (
                <img
                  src={assetUrl(p.logo)}
                  alt={p.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50 rounded">
                  <i className="ri-building-line text-gray-300 text-lg"></i>
                </div>
              )}
            </div>
            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{p.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PartnersSection({ partners }: { partners: { name: string; logo?: string }[] }) {
  const [isPaused, setIsPaused] = useState(false);

  const rows = [
    partners.slice(0, 7),
    partners.slice(7, 14),
    partners.slice(14, 21),
    partners.slice(21, 28),
  ];

  const speeds = [0.5, 0.4, 0.55, 0.45];
  // Each row starts at a different offset so they appear staggered horizontally
  const offsets = [0, 80, 40, 120];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
            <span className="text-[#1a56db] text-xs font-medium">合作伙伴</span>
          </div>
          <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c]">携手行业领军品牌</h2>
          <p className="text-sm text-gray-500 mt-3 max-w-xl mx-auto">与国内外顶尖安全与IT厂商深度合作，为客户提供最优质的产品与解决方案</p>
        </div>
      </div>
      <div
        className="border-t border-gray-100"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {rows.map((row, i) => (
          <MarqueeRow
            key={i}
            items={row}
            speed={speeds[i]}
            isPaused={isPaused}
            offsetStart={offsets[i]}
          />
        ))}
      </div>
    </section>
  );
}

export default function AboutPage() {
  const [companyIntro, setCompanyIntro] = useState<any>(
    () => readAboutPreload()?.companyIntro ?? { title: '', desc1: '', desc2: '', image: '' },
  );
  const [milestones, setMilestones] = useState<any[]>(
    () => readAboutPreload()?.milestones?.map((m: any) => ({ year: m.year, event: m.event })) ?? [],
  );
  const [certifications, setCertifications] = useState<any[]>(
    () =>
      readAboutPreload()?.certifications?.map((c: any) => ({
        name: c.name,
        icon: c.icon || 'ri-award-line',
        desc: c.description || '',
      })) ?? [],
  );
  const [teamStats, setTeamStats] = useState<any[]>(
    () => readAboutPreload()?.teamStats?.map((t: any) => ({ value: t.value, label: t.name })) ?? [],
  );
  const [coreValues, setCoreValues] = useState<any[]>(
    () =>
      readAboutPreload()?.coreValues?.map((v: any) => ({
        icon: v.icon,
        title: v.name,
        desc: v.description || v.desc,
      })) ?? [],
  );
  const [partners, setPartners] = useState<any[]>(
    () => readAboutPreload()?.partners?.map((p: any) => ({ name: p.name, logo: p.logo })) ?? [],
  );
  const [contact, setContact] = useState<any>(() => readAboutPreload()?.contact ?? {});

  useSEO({
    title: "关于欧格登 | 十年深耕企业级安全与智能化服务",
    description:
      "欧格登是一家专注信息安全、IT基础架构、弱电智能化的综合解决方案提供商。100+持证安全工程师，10年+行业经验，服务500+行业头部客户，通过ISO27001、等保三级等多项权威认证。",
    keywords: [
      "欧格登公司",
      "欧格登介绍",
      "欧格登简介",
      "欧格登科技有限公司",
      "ISO27001",
      "等保认证",
      "安全服务商",
    ],
    path: "/about",
    pageType: "ABOUT",
    jsonLd: [
      buildBreadcrumbLd([
        { name: "首页", path: "/" },
        { name: "关于我们", path: "/about" },
      ]),
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于欧格登",
        url: `${SITE_ORIGIN}/about`,
        description:
          "欧格登是一家专注信息安全、IT基础架构、弱电智能化的综合解决方案提供商。",
      },
    ],
  });

  useEffect(() => {
    getAboutData().then((res: any) => {
      if (res.companyIntro) setCompanyIntro(res.companyIntro);
      if (res.milestones) setMilestones(res.milestones.map((m: any) => ({ year: m.year, event: m.event })));
      if (res.certifications) setCertifications(res.certifications.map((c: any) => ({ name: c.name, icon: c.icon || 'ri-award-line', desc: c.description || '' })));
      if (res.teamStats) setTeamStats(res.teamStats.map((t: any) => ({ value: t.value, label: t.name })));
      if (res.coreValues) setCoreValues(res.coreValues.map((v: any) => ({ icon: v.icon, title: v.name, desc: v.description || v.desc })));
      if (res.partners) setPartners(res.partners.map((p: any) => ({ name: p.name, logo: p.logo })));
      if (res.contact) setContact(res.contact);
    }).catch(() => {});
  }, []);
  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative bg-[#0d1f3c] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20corporate%20company%20headquarters%20building%2C%20modern%20technology%20company%20office%2C%20enterprise%20business%20environment%2C%20navy%20blue%20architecture%2C%20clean%20professional%20atmosphere&width=1920&height=500&seq=abouthero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/60 to-[#0d1f3c]"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/20 border border-[#1a56db]/40 rounded-full mb-5">
            <span className="text-[#4db8ff] text-xs font-medium">关于我们</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            专注信息安全，护航数字未来
          </h1>
          <p className="text-gray-400 text-sm lg:text-base max-w-2xl mx-auto">
            深耕信息安全领域10余年，为500+企业提供专业安全保障服务
          </p>
        </div>
      </section>

      {/* Company Intro */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-5">
                <span className="text-[#1a56db] text-xs font-medium">公司简介</span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-5 leading-snug">
                {companyIntro.title}
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{companyIntro.desc1}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">{companyIntro.desc2}</p>
              {/* Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                {teamStats.map((s) => (
                  <div key={s.label} className="text-center p-4 bg-[#f8fafc] rounded-xl border border-gray-100">
                    <div className="text-2xl font-bold text-[#1a56db] mb-1">{s.value}</div>
                    <div className="text-xs text-gray-500">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 w-full lg:w-[500px] h-64 lg:h-80 rounded-2xl overflow-hidden">
              <img
                src={assetUrl(companyIntro.image)}
                alt="公司简介"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
              <span className="text-[#1a56db] text-xs font-medium">核心价值观</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c]">我们的价值理念</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {coreValues.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-6 border border-gray-100 text-center hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#1a56db]/8 mx-auto mb-4">
                  <i className={`${v.icon} text-2xl text-[#1a56db]`}></i>
                </div>
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-2">{v.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
              <span className="text-[#1a56db] text-xs font-medium">发展历程</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c]">十年深耕，砥砺前行</h2>
          </div>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gray-200 hidden lg:block"></div>
            <div className="space-y-6 lg:space-y-0">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-8 ${
                    i % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                  }`}
                >
                  <div className={`flex-1 ${i % 2 === 0 ? "lg:text-right" : "lg:text-left"}`}>
                    <div className={`inline-block bg-white rounded-xl p-5 border border-gray-100 hover:border-[#1a56db]/20 transition-all duration-200 ${i % 2 === 0 ? "" : ""}`}>
                      <div className="text-[#1a56db] font-bold text-lg mb-1">{m.year}</div>
                      <p className="text-sm text-gray-600">{m.event}</p>
                    </div>
                  </div>
                  {/* Center dot */}
                  <div className="hidden lg:flex w-4 h-4 rounded-full bg-[#1a56db] border-4 border-white ring-2 ring-[#1a56db]/20 flex-shrink-0 z-10"></div>
                  <div className="flex-1 hidden lg:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-[#f8fafc]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1a56db]/8 border border-[#1a56db]/20 rounded-full mb-4">
              <span className="text-[#1a56db] text-xs font-medium">资质荣誉</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c]">权威认证，专业保障</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {certifications.map((cert) => (
              <div key={cert.name} className="bg-white rounded-xl p-5 border border-gray-100 text-center hover:border-[#1a56db]/20 hover:-translate-y-0.5 transition-all duration-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a56db]/8 mx-auto mb-3">
                  <i className={`${cert.icon} text-xl text-[#1a56db]`}></i>
                </div>
                <div className="text-sm font-bold text-[#0d1f3c] mb-1">{cert.name}</div>
                <div className="text-xs text-gray-400 leading-snug">{cert.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <PartnersSection partners={partners} />

      {/* Contact Info */}
      <section className="py-16 bg-[#F4F7FC]" id="about-contact">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#0d1f3c] mb-3">联系我们</h2>
            <p className="text-sm text-[#555555]">欢迎与我们联系，共同探讨您的安全需求</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: "ri-phone-line", title: "服务热线", value: contact.phone || "400-888-9999", sub: "工作日 9:00-18:00" },
              { icon: "ri-mail-line", title: "邮箱", value: contact.email || "contact@securetech.com", sub: "1个工作日内回复" },
              { icon: "ri-map-pin-line", title: "总部地址", value: contact.address || "北京市海淀区中关村科技园区", sub: "欢迎预约参观" },
              { icon: "ri-wechat-line", title: "微信公众号", value: contact.wechat || "SecureTech安全", sub: "扫码关注获取资讯" },
            ].map((item) => (
              <div key={item.title} className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:border-[#1a56db]/25 transition-all duration-200">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#1a56db]/8 mx-auto mb-3">
                  <i className={`${item.icon} text-xl text-[#1a56db]`}></i>
                </div>
                <div className="text-xs text-gray-400 mb-1">{item.title}</div>
                <div className="text-sm font-semibold text-[#0d1f3c] mb-1">{item.value}</div>
                <div className="text-xs text-gray-400">{item.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FaqBlock scope="ABOUT" />
    </main>
  );
}
